import { CheckCircle2, CreditCard, ExternalLink, Clock, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { useSubscription, useCheckoutUrl, usePortalUrl } from "../hooks/useDating";

const BENEFITS = [
  "Unbegrenzt Matches und Nachrichten",
  "Psychologisches Werte-Matching mit 15 Dimensionen",
  "Deine Daten bleiben in Deutschland (Hetzner, Falkenstein)",
  "Kein Gender-Preis, keine algorithmische Bevorzugung",
  "Volle Kontrolle: Blocken, Melden, Datenexport, Kontolöschung",
];

export function Abo() {
  const { data: sub, isLoading } = useSubscription();
  const checkout = useCheckoutUrl();
  const portal = usePortalUrl();

  const trialDaysLeft = sub?.trialEndsAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(sub.trialEndsAt).getTime() - Date.now()) / (86400 * 1000),
        ),
      )
    : 0;

  async function startCheckout() {
    try {
      const { url } = await checkout.mutateAsync();
      window.location.href = url;
    } catch {
      // Fehler-Toast kommt später
    }
  }

  async function openPortal() {
    try {
      const { url } = await portal.mutateAsync();
      window.location.href = url;
    } catch {
      // Fehler-Toast kommt später
    }
  }

  return (
    <AppShell>
      <section className="container max-w-2xl pt-6">
        <h1 className="text-2xl font-bold tracking-tight">Dein Abo</h1>
        <p className="mt-1 text-sm text-ink-500">
          Transparent, fair, für alle gleich.
        </p>

        {isLoading ? (
          <div className="flex min-h-[30vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          </div>
        ) : (
          <div className="relative mt-6 overflow-hidden rounded-[2rem] bg-brand-gradient p-8 text-white shadow-glow">
            <div className="pointer-events-none absolute -top-16 -right-16 h-60 w-60 rounded-full bg-white/20 blur-3xl" />
            <div className="relative">
              {sub?.status === "trial" ? (
                <>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm">
                    <Clock className="h-3.5 w-3.5" />
                    Probemonat läuft
                  </span>
                  <p className="mt-4 text-5xl font-bold tabular-nums">
                    {trialDaysLeft}{" "}
                    <span className="text-xl font-semibold text-white/80">
                      Tage übrig
                    </span>
                  </p>
                  <p className="mt-2 text-white/90">
                    Danach 5 € im Monat. Für alle gleich. Jederzeit kündbar.
                  </p>
                  <button
                    type="button"
                    onClick={startCheckout}
                    disabled={checkout.isPending}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-brand-600 shadow-soft transition active:scale-[0.98] disabled:opacity-50"
                  >
                    {checkout.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Zahlungsmethode hinterlegen
                  </button>
                </>
              ) : sub?.status === "active" ? (
                <>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Abo aktiv
                  </span>
                  <p className="mt-4 text-3xl font-bold">
                    5 € / Monat
                  </p>
                  {sub.currentPeriodEnd && (
                    <p className="mt-2 text-white/90">
                      Nächste Abbuchung:{" "}
                      {new Date(sub.currentPeriodEnd).toLocaleDateString("de-DE")}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold">
                    Probemonat abgelaufen
                  </p>
                  <p className="mt-2 text-white/90">
                    Starte dein Abo für 5 € pro Monat.
                  </p>
                  <button
                    type="button"
                    onClick={startCheckout}
                    disabled={checkout.isPending}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-brand-600 shadow-soft transition active:scale-[0.98] disabled:opacity-50"
                  >
                    <CreditCard className="h-4 w-4" />
                    Jetzt abonnieren
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-ink-500">
            Was im Abo drin ist
          </h2>
          <ul className="card divide-y divide-border p-2">
            {BENEFITS.map((item) => (
              <li key={item} className="flex items-start gap-3 px-4 py-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
                <span className="text-sm text-ink-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {sub?.status === "active" && (
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={openPortal}
              disabled={portal.isPending}
              className="btn-secondary flex-1 disabled:opacity-50"
            >
              {portal.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Stripe-Kundenportal öffnen
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-ink-500">
          Abrechnung über Stripe EU · SEPA + Karte · 19% USt. inklusive
        </p>
      </section>
    </AppShell>
  );
}
